import { supabase } from '@/lib/supabase';
import { PARAMETRIC_MODELS } from '@/lib/utils';
import { Content, Conversation, Message, Model } from '@shared/types';
import { UseMutateAsyncFunction, useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import {
  SUPIN_URL,
  SUPIN_TIMEOUT_MS,
  INLET_LABELS,
  type SupinRunnerResponse,
} from '@/config/supin';
import { SUSP_TYPE_LABELS, type SharkRunnerResponse } from '@/config/shark';
import {
  errToMessage,
  explainComputeError,
  resolveOutputFormat,
} from './outputFormat.ts';
import { useInsertMessageMutation } from './crud.ts';
import {
  useMeshGenChatMutation,
  useParametricChatMutation,
} from './streaming.ts';

export function useSendContentMutation({
  conversation,
}: {
  conversation: Pick<
    Conversation,
    'id' | 'user_id' | 'settings' | 'current_message_leaf_id' | 'type'
  >;
}) {
  const { mutateAsync: insertMessageAsync } = useInsertMessageMutation();
  const { mutateAsync: sendToMeshGenChat } = useMeshGenChatMutation({
    conversationId: conversation.id,
  });

  const { mutateAsync: sendToParametricChat } = useParametricChatMutation({
    conversationId: conversation.id,
  });

  return useMutation({
    mutationKey: ['send-content', conversation.id],
    mutationFn: async (content: Content) => {
      // Handle image uploads and create message
      const databaseOperations = [];

      if (content.images && content.images.length > 0) {
        // Create database entries for images and move them to conversation folder
        const imageOperations = content.images.map(async (imageId) => {
          // Create the image record in the database
          const { error: imageError } = await supabase.from('images').upsert(
            {
              id: imageId,
              prompt: {
                text: 'User uploaded image',
              },
              status: 'success',
              user_id: conversation.user_id,
              conversation_id: conversation.id,
            },
            {
              onConflict: 'id',
              ignoreDuplicates: true,
            },
          );

          if (imageError) throw imageError;
        });
        databaseOperations.push(...imageOperations);
      }

      if (content.mesh) {
        const meshOperation = supabase
          .from('meshes')
          .upsert(
            {
              id: content.mesh.id,
              conversation_id: conversation.id,
              user_id: conversation.user_id,
              status: 'success',
              prompt: {
                text: 'User uploaded mesh',
              },
              file_type: content.mesh.fileType,
            },
            {
              onConflict: 'id',
              ignoreDuplicates: true,
            },
          )
          .then(({ error: meshError }) => {
            if (meshError) throw meshError;
          });

        databaseOperations.push(meshOperation);
      }

      await Promise.all(databaseOperations);

      const userMessage = await insertMessageAsync({
        role: 'user',
        content,
        parent_message_id: conversation.current_message_leaf_id ?? null,
        conversation_id: conversation.id,
      });

      if (conversation.settings?.vehicleSubtype === 'tubeframe-joint') {
        const vp = content.vehicleParams;
        const od = vp?.od ?? '1.5';
        const id = vp?.id ?? '1.375';
        const angle = vp?.angle ?? '45';
        const mating = vp?.matingSurface ?? 'straight-wall';
        const units = vp?.units ?? 'in';
        const unitLabel = units === 'in' ? 'inches' : 'mm';

        const generatedCode = `from build123d import *

# Tubeframe Joint Parameters
tube_od = ${od}        # ${unitLabel} — outer diameter
tube_id = ${id}        # ${unitLabel} — inner diameter
joint_angle = ${angle} # degrees
mating_surface = "${mating}"

wall_thickness = (tube_od - tube_id) / 2

with BuildPart() as joint:
    # Primary tube barrel
    with BuildSketch(Plane.XZ) as profile:
        Circle(tube_od / 2)
        Circle(tube_id / 2, mode=Mode.SUBTRACT)
    extrude(amount=tube_od * 5)

    # Branching arm at joint_angle
    rotation = Rotation(0, joint_angle, 0)
    with BuildSketch(Plane.XZ.rotated((0, joint_angle, 0))) as branch:
        Circle(tube_od / 2)
        Circle(tube_id / 2, mode=Mode.SUBTRACT)
    extrude(amount=tube_od * 4)

    # Mating surface treatment: ${mating}
    if mating_surface == "threaded":
        thread_pitch = wall_thickness * 0.6
        helix(pitch=thread_pitch, height=tube_od, radius=tube_od / 2)
    elif mating_surface == "lattice":
        with GridLocations(tube_od * 0.3, tube_od * 0.3, 3, 3) as locs:
            Cylinder(radius=wall_thickness * 0.2, height=wall_thickness)

show_object(joint)`;

        const matingSurfaceOptions = [
          { value: 'straight-wall', label: 'Straight Wall' },
          { value: 'lattice', label: 'Lattice' },
          { value: 'threaded', label: 'Threaded' },
        ];

        await insertMessageAsync({
          role: 'assistant',
          content: {
            artifact: {
              title: 'Tubeframe Joint',
              version: '1.0',
              code: generatedCode,
              static_stl: 'tubeframe-joint.stl',
              parameters: [
                {
                  name: 'tube_od',
                  displayName: 'Tube O.D.',
                  value: od,
                  defaultValue: od,
                  type: 'number' as const,
                  description: `Tube outer diameter (${unitLabel})`,
                },
                {
                  name: 'tube_id',
                  displayName: 'Tube I.D.',
                  value: id,
                  defaultValue: id,
                  type: 'number' as const,
                  description: `Tube inner diameter (${unitLabel})`,
                },
                {
                  name: 'joint_angle',
                  displayName: 'Joint Angle',
                  value: angle,
                  defaultValue: angle,
                  type: 'number' as const,
                  description: 'Joint angle (degrees)',
                },
                {
                  name: 'mating_surface',
                  displayName: 'Mating Surface',
                  value: mating,
                  defaultValue: mating,
                  type: 'string' as const,
                  options: matingSurfaceOptions,
                },
              ],
            },
          },
          parent_message_id: userMessage.id,
          conversation_id: conversation.id,
        });
      } else if (
        conversation.settings?.vehicleSubtype === 'nasa-supersonic-inlet'
      ) {
        const sp = content.supinParams;
        if (!sp) throw new Error('SUPIN parameters missing from content');

        // Endpoint, response shape, and inlet labels live in
        // src/config/supin.ts so the URL is env-overridable and the
        // contract stays in lockstep with supin-microservice/main.py.

        // Send the supinParams shape verbatim; the Pydantic model on the
        // server picks out exactly the fields it needs and ignores extras.
        // Bound the request so a slow/stalled run (K2/K4 are the heaviest)
        // can't hang the UI forever. The browser fetch has no default timeout,
        // so without this an unfinished SUPIN call shows up as a permanent
        // spinner — the "K2 never solves" symptom in issue #26.
        const supinAbort = new AbortController();
        const supinTimer = setTimeout(
          () => supinAbort.abort(),
          SUPIN_TIMEOUT_MS,
        );
        try {
          const supinToken = (await supabase.auth.getSession()).data.session
            ?.access_token;
          const resp = await fetch(SUPIN_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(supinToken ? { Authorization: `Bearer ${supinToken}` } : {}),
            },
            body: JSON.stringify(sp),
            signal: supinAbort.signal,
          });

          if (!resp.ok) {
            // 401/403 here is almost always a cross-project JWT mismatch, not a
            // bad token: SUPIN is the only kernel the browser calls directly
            // (no edge function), so it ships the user's Supabase JWT and the
            // runner validates it against its OWN {SUPABASE_URL}/auth/v1/user.
            // The deployed runner is wired to PROD Supabase, so a dev frontend
            // pointed at STAGING gets a token prod can't resolve → 401. Surface
            // the real cause + fix instead of a bare status code.
            if (resp.status === 401 || resp.status === 403) {
              // explainComputeError appends the probable-cause summary + the
              // postmortem doc pointer (the established SUPIN-401 root cause).
              throw new Error(
                explainComputeError(
                  `SUPIN auth failed (HTTP ${resp.status}): your session is ` +
                    `from a different Supabase project than the SUPIN runner ` +
                    `validates against. In dev, point VITE_SUPIN_URL at a ` +
                    `staging SUPIN runner, or test against prod. See ` +
                    `docs/deploy/supin-staging-runner.md.`,
                ),
              );
            }
            throw new Error(
              explainComputeError(`SUPIN service returned ${resp.status}`),
            );
          }

          const result = (await resp.json()) as SupinRunnerResponse;

          if (result.error) {
            throw new Error(result.error);
          }

          // Build the artifact param list from the input params + parsed
          // SUPIN metrics. The viewer uses this to populate its sidebar.
          const parameters: import('@shared/types').Parameter[] = [
            {
              name: 'inletType',
              displayName: 'Inlet Type',
              value: sp.inletType,
              defaultValue: sp.inletType,
              type: 'string' as const,
            },
            {
              name: 'mach',
              displayName: 'Mach',
              value: sp.mach,
              defaultValue: sp.mach,
              type: 'number' as const,
            },
            ...(sp.altitude
              ? [
                  {
                    name: 'altitude',
                    displayName: 'Altitude',
                    value: sp.altitude,
                    defaultValue: sp.altitude,
                    type: 'number' as const,
                    description: 'ft',
                  },
                ]
              : []),
            {
              name: 'engineFlow',
              displayName: 'Engine Flow',
              value: sp.engineFlow,
              defaultValue: sp.engineFlow,
              type: 'number' as const,
              description: 'lbm/s',
            },
            // Computed metrics — read-only display rows.
            ...Object.entries(result.metrics ?? {}).flatMap(([k, v]) =>
              v == null
                ? []
                : [
                    {
                      name: `metric_${k}`,
                      displayName: k,
                      value: String(v),
                      defaultValue: String(v),
                      type: 'string' as const,
                      group: 'Computed',
                    },
                  ],
            ),
          ];

          // Keystone: offload the SUPIN STL to the private `stl-tiers` bucket
          // (URL ref) instead of inlining ~MB of base64 into `content`. Unlike
          // the build123d path (server-side via parametric-chat + service role),
          // SUPIN writes its message from the browser, so the upload runs on the
          // authenticated client — permitted by the `stl-tiers` INSERT policy
          // scoped to the user's own conversations. Falls back to inline if the
          // upload fails, so a Storage hiccup never loses the mesh.
          let supinStlPath: string | undefined;
          const supinStlB64 = result.stl_b64;
          if (supinStlB64) {
            try {
              const bin = atob(supinStlB64);
              const bytes = new Uint8Array(bin.length);
              for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
              const path = `parametric/${conversation.id}/${crypto.randomUUID()}.stl`;
              const { error: upErr } = await supabase.storage
                .from('stl-tiers')
                .upload(path, bytes, {
                  contentType: 'model/stl',
                  upsert: true,
                });
              if (upErr) {
                console.warn(
                  '[supin] STL upload failed, keeping inline:',
                  upErr.message,
                );
              } else {
                supinStlPath = path;
              }
            } catch (e) {
              console.warn('[supin] STL upload threw, keeping inline:', e);
            }
          }

          await insertMessageAsync({
            role: 'assistant',
            content: {
              // SUPIN runs are Fortran-solver-driven — no LLM is involved.
              // Stamp the message with the canonical 'supin' sentinel. The
              // chat bubble (AssistantMessage) renders "NASA SUPIN" instead
              // of whichever LLM happened to be selected in the sidebar at
              // the time (it detects SUPIN via artifact.supin_in). The
              // dropdown sidebar label is overridden client-side in
              // FeatureTreeSidebar; this stamp is what keeps the chat
              // bubble in sync.
              model: 'supin',
              artifact: {
                title: `NASA SUPIN ${INLET_LABELS[sp.inletType] ?? sp.inletType}`,
                version: '1.0',
                // Important: keep `code` EMPTY for SUPIN artifacts. The
                // Build123dPreview re-run useEffect fires whenever `code`
                // changes and POSTs to the build123d executor, which would
                // fail on SUPIN.in. The generated input file lives in
                // `supin_in` instead so the panel can display it without
                // triggering the re-run path.
                code: '',
                supin_in: result.supin_in ?? '',
                // Storage ref when the upload succeeded; inline only as fallback.
                stl_b64: supinStlPath ? undefined : supinStlB64,
                stl_path: supinStlPath,
                parameters,
              },
            },
            parent_message_id: userMessage.id,
            conversation_id: conversation.id,
          });
        } catch (err) {
          Sentry.captureException(err, {
            extra: {
              hook: 'useSendContentMutation/supin',
              inletType: sp.inletType,
            },
          });
          // An AbortError here is our own timeout firing, not a server error.
          // Give the user an actionable message instead of a bare
          // "The user aborted a request." DOMException.
          const isAbort =
            err instanceof DOMException && err.name === 'AbortError';
          const errMsg = isAbort
            ? `SUPIN ${sp.inletType} timed out after ${Math.round(
                SUPIN_TIMEOUT_MS / 1000,
              )}s. The 2D cases (K2/K4) build the heaviest mesh — retry, or ` +
              `lower "Ramp stages". If it keeps timing out the runner is cold; ` +
              `try again once it's warm.`
            : errToMessage(err);
          await insertMessageAsync({
            role: 'assistant',
            content: {
              // Same sentinel as the success path — even a failed SUPIN run
              // wasn't an LLM run, so the chip should still read
              // "NASA SUPIN" rather than the user's selected model.
              model: 'supin',
              // Keep the short `text` for the chat-bubble preview line
              // (existing UX), but ALSO attach the structured payload so
              // the ErrorLogBubble + Expand overlay can render with the
              // copy-to-clipboard affordance.
              text: `SUPIN generation failed: ${errMsg}`,
              errorLog: {
                kind: 'preset' as const,
                source: 'supin',
                code: sp.inletType,
                message: errMsg,
              },
            },
            parent_message_id: userMessage.id,
            conversation_id: conversation.id,
          });
        } finally {
          clearTimeout(supinTimer);
        }
      } else if (
        conversation.settings?.vehicleSubtype === 'suspension-kinematics'
      ) {
        const skp = content.suspensionKinematicsParams;
        if (!skp)
          throw new Error(
            'Suspension kinematics parameters missing from content',
          );

        try {
          // Through the suspension-kinematics edge fn (auth + token billing +
          // X-TK-Auth relay) — the browser never calls the solver directly
          // (rule C). The edge fn parses the SKP form strings itself; this
          // path renders its own artifact, so the LLM summary is skipped.
          const { data: sharkData, error: invokeError } =
            await supabase.functions.invoke('suspension-kinematics', {
              body: { ...skp, skipSummary: true },
            });

          if (invokeError) {
            throw new Error(`JAWS solver call failed: ${invokeError.message}`);
          }

          const result = (sharkData ?? {}) as SharkRunnerResponse;

          if (result.error) {
            throw new Error(result.error);
          }

          const kin = result.kinematics;
          const suspLabel = SUSP_TYPE_LABELS[skp.suspType] ?? skp.suspType;
          const axleLabel = skp.axle === 'front' ? 'Front' : 'Rear';

          await insertMessageAsync({
            role: 'assistant',
            content: {
              artifact: {
                title: `Suspension Kinematics — ${suspLabel} ${axleLabel}`,
                version: '1.0',
                code: '',
                parameters: [
                  {
                    name: 'suspType',
                    displayName: 'Suspension Type',
                    value: suspLabel,
                    defaultValue: suspLabel,
                    type: 'string' as const,
                  },
                  {
                    name: 'axle',
                    displayName: 'Axle',
                    value: axleLabel,
                    defaultValue: axleLabel,
                    type: 'string' as const,
                  },
                  {
                    name: 'units',
                    displayName: 'Units',
                    value: skp.units,
                    defaultValue: skp.units,
                    type: 'string' as const,
                  },
                  {
                    name: 'travelRange',
                    displayName: 'Travel Range',
                    value: `${skp.travelMin} to ${skp.travelMax} ${skp.units}`,
                    defaultValue: `${skp.travelMin} to ${skp.travelMax} ${skp.units}`,
                    type: 'string' as const,
                    group: 'Analysis',
                  },
                  {
                    name: 'steerRange',
                    displayName: 'Steer Range',
                    value: `${skp.steerMin}° to ${skp.steerMax}°`,
                    defaultValue: `${skp.steerMin}° to ${skp.steerMax}°`,
                    type: 'string' as const,
                    group: 'Analysis',
                  },
                ],
              },
              kinematicsData: kin,
            },
            parent_message_id: userMessage.id,
            conversation_id: conversation.id,
          });
        } catch (err) {
          Sentry.captureException(err, {
            extra: {
              hook: 'useSendContentMutation/suspension-kinematics',
              suspType: content.suspensionKinematicsParams?.suspType,
            },
          });
          const errMsg = err instanceof Error ? err.message : String(err);
          await insertMessageAsync({
            role: 'assistant',
            content: {
              text: `Suspension kinematics analysis failed: ${errMsg}`,
              errorLog: {
                kind: 'preset' as const,
                source: 'shark',
                code: content.suspensionKinematicsParams?.suspType,
                message: errMsg,
              },
            },
            parent_message_id: userMessage.id,
            conversation_id: conversation.id,
          });
        }
      } else if (conversation.type === 'imagetocad') {
        await sendToMeshGenChat({
          model: content.model ?? conversation.settings?.model ?? 'quality',
          messageId: userMessage.id,
          conversationId: conversation.id,
        });
      } else {
        await sendToParametricChat({
          model:
            content.model ??
            conversation.settings?.model ??
            PARAMETRIC_MODELS[0].id,
          messageId: userMessage.id,
          conversationId: conversation.id,
          outputFormat: resolveOutputFormat(conversation, content),
        });
      }
    },
  });
}

export function useEditMessageMutation({
  conversation,
}: {
  conversation: Conversation;
}) {
  const { mutateAsync: insertMessageAsync } = useInsertMessageMutation();

  const { mutateAsync: sendToMeshGenChat } = useMeshGenChatMutation({
    conversationId: conversation.id,
  });

  const { mutateAsync: sendToParametricChat } = useParametricChatMutation({
    conversationId: conversation.id,
  });

  return useMutation({
    mutationKey: ['edit-message', conversation.id],
    mutationFn: async (updatedMessage: Message) => {
      const userMessage = await insertMessageAsync({
        role: updatedMessage.role,
        content: updatedMessage.content,
        parent_message_id: updatedMessage.parent_message_id ?? null,
        conversation_id: conversation.id,
      });

      if (conversation.type === 'imagetocad') {
        sendToMeshGenChat({
          model: conversation.settings?.model ?? 'quality',
          messageId: userMessage.id,
          conversationId: conversation.id,
        });
      } else {
        sendToParametricChat({
          model: conversation.settings?.model ?? PARAMETRIC_MODELS[0].id,
          messageId: userMessage.id,
          conversationId: conversation.id,
          outputFormat: resolveOutputFormat(conversation),
        });
      }
    },
    onError: (error, updatedMessage) => {
      Sentry.captureException(error, {
        extra: {
          hook: 'useEditMessageMutation',
          updatedMessage,
          conversationId: conversation.id,
        },
      });
    },
  });
}

export function useRetryMessageMutation({
  conversation,
  updateConversationAsync,
}: {
  conversation: Conversation;
  updateConversationAsync?: UseMutateAsyncFunction<
    Conversation,
    Error,
    Conversation
  >;
}) {
  const { mutateAsync: sendToMeshGenChat } = useMeshGenChatMutation({
    conversationId: conversation.id,
  });

  const { mutateAsync: sendToParametricChat } = useParametricChatMutation({
    conversationId: conversation.id,
  });

  return useMutation({
    mutationKey: ['retry-message', conversation.id],
    mutationFn: async ({ model, id }: { model: Model; id: string }) => {
      if (!updateConversationAsync) {
        throw new Error('Cannot update conversation');
      }

      await updateConversationAsync({
        ...conversation,
        settings: {
          ...(typeof conversation.settings === 'object'
            ? conversation.settings
            : {}),
          model: model,
        },
        current_message_leaf_id: id,
      });

      if (conversation.type === 'imagetocad') {
        sendToMeshGenChat({
          model: model,
          messageId: id,
          conversationId: conversation.id,
        });
      } else {
        sendToParametricChat({
          model: model,
          messageId: id,
          conversationId: conversation.id,
          outputFormat: resolveOutputFormat(conversation),
        });
      }
    },
    onError: (error, { model, id }) => {
      Sentry.captureException(error, {
        extra: {
          hook: 'useRetryMessageMutation',
          conversationId: conversation.id,
          model,
          id,
        },
      });
    },
  });
}
